import { message, Tree } from 'antd';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { AiOutlineExpandAlt } from 'react-icons/ai';
import { DeleteIconSm, EditIcon } from '../../Globle/Icon';
import { useMainContext } from '../../../hooks/useMainContext';
import { IoIosArrowForward, IoIosArrowDown } from 'react-icons/io';
import DeleteModel from '../../Globle/DeleteModel';

const ClosetData = observer(({ closets }) => {
    const { viewManager, closetManager, drawingManager } = useMainContext();
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [isModelOpen, setIsModelOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemType, setItemType] = useState(null);

    const handleDelete = (item) => {
        if (drawingManager.isDrawingModeOn) {
            message.error("Can't do this operation while drawing mode is on.");
            return;
        }
        setIsModelOpen(true);
        setSelectedItem(item);
    };
    const handleCancel = () => {
        setIsModelOpen(false);
        setSelectedItem(null);
        setItemType(null);
    };
    const handleDeleteCloset = () => {
        closetManager.removeClosetById(selectedItem._id);
        closetManager.drawNewClosets();
        message.success('Closet deleted successfully.');
        handleCancel();
    };
    const handleDeleteAttachment = () => {
        closetManager.removeAttachmentById(selectedItem._id);

        handleCancel();
    };
    const handleDeleteObstacle = () => {
        closetManager.removeObstacleById(selectedItem._id);
        closetManager.drawNewClosets();
        message.success('Obstacle deleted successfully.');
        handleCancel();
    };
    const handleDeleteInternalWall = () => {
        closetManager.removeInternalWallById(selectedItem._id);
        closetManager.drawNewClosets();
        message.success('Internal Wall deleted successfully.');
        handleCancel();
    };
    const handleConfirmDelete = () => {
        switch (itemType) {
            case 'closet':
                handleDeleteCloset();
                break;
            case 'attachment':
                handleDeleteAttachment();
                break;
            case 'obstacle':
                handleDeleteObstacle();
                break;
            case 'internalWall':
                handleDeleteInternalWall();
                break;
            default:
                break;
        }
    };
    const handleFitToScreenCloset = async (closetId) => {
        await closetManager.showClosets(closetId);
    };

    const handleEditCloset = (closetId) => {
        viewManager.setClosetForm(true);
        closetManager.setSelectedClosetById(closetId);
    };

    const treeData = closets.map((closet) => {
        const attachmentChildren = (closet.attachments || []).map(
            (item, index) => ({
                title: (
                    <TreeNode
                        item={item}
                        title={`${item._attachmentType} - ${item._type} - ${item._opening}`}
                        handleEdit={() => {
                            const selectedAttachment =
                                closetManager.attachments.find(
                                    (attachment) => attachment._id === item._id,
                                );
                            if (!selectedAttachment) return;
                            closetManager.setSelectedAttachment(
                                selectedAttachment,
                            );
                            closetManager.setAttachmentEditMode(true);
                        }}
                        handleDelete={() => {
                            handleDelete(item);
                            setItemType('attachment');
                        }}
                    />
                ),
                key: `closet-${closet._id}-attachment-${index}`,
                type: 'attachment',
                className: 'custom-closet-attachment-node',
            }),
        );

        const obstacleChildren = (closet.obstacles || []).map(
            (item, index) => ({
                title: (
                    <TreeNode
                        item={item}
                        title={`Obstacle - ${item._obstacleType}`}
                        handleEdit={() => {
                            const selectedObstacle =
                                closetManager.obstacles.find(
                                    (obstacle) => obstacle._id === item._id,
                                );
                            if (!selectedObstacle) return;
                            closetManager.setObstacleForm(true);
                            closetManager.setObstacleMode(true);
                            closetManager.setObstacleEditMode(true);
                            closetManager.setSelectedObstacle(selectedObstacle);
                        }}
                        handleDelete={() => {
                            handleDelete(item);
                            setItemType('obstacle');
                        }}
                    />
                ),
                key: `closet-${closet._id}-obstacle-${index}`,
                type: 'obstacle',
                className: 'custom-closet-attachment-node',
            }),
        );

        const wallChildren = (closet.internalWalls || []).map(
            (item, index) => ({
                title: (
                    <TreeNode
                        item={item}
                        title={`Internal Wall - ${index + 1}`}
                        handleDelete={() => {
                            handleDelete(item);
                            setItemType('internalWall');
                        }}
                        handleEdit={() => {
                            const selectedInternalWall =
                                closetManager.internalWalls.find(
                                    (wall) => wall._id === item._id,
                                );
                            if (!selectedInternalWall) return;
                            closetManager.setInternalForm(true);
                            closetManager.setInternalWallData(
                                selectedInternalWall,
                            );
                        }}
                    />
                ),
                key: `closet-${closet._id}-wall-${index}`,
                type: 'internalWall',
                className: 'custom-closet-attachment-node',
            }),
        );

        return {
            title: (
                <TreeNode
                    item={closet}
                    title={closet._name}
                    textClassName="text-[16px] font-semibold !text-theme-black"
                    handleDelete={() => {
                        handleDelete(closet);
                        setItemType('closet');
                    }}
                    handleFitToScreen={() =>
                        handleFitToScreenCloset(closet._id)
                    }
                    handleEdit={() => {
                        handleEditCloset(closet._id);
                    }}
                    isFitToScreen={true}
                />
            ),
            className: 'custom-closet-node',
            key: `closet-${closet._id}`,
            pageNumber: closet.pageNo,
            children: [
                ...attachmentChildren,
                ...obstacleChildren,
                ...wallChildren,
            ],
            type: 'closet',
        };
    });
    if (treeData.length === 0) {
        return (
            <div>
                <p className="font-favorit text-[14px] font-[400] leading-[21px] text-theme-stroke text-center">
                    No closets found in this unit
                </p>
            </div>
        );
    }
    return (
        <>
            <Tree
                onExpand={setExpandedKeys}
                expandedKeys={expandedKeys}
                treeData={treeData}
                className="custom-tree !font-favorit"
                defaultExpandAll={true}
                switcherIcon={({ expanded }) =>
                    expanded ? (
                        <IoIosArrowDown className="text-[20px] text-theme-black" />
                    ) : (
                        <IoIosArrowForward className="text-[20px] text-theme-black" />
                    )
                }
            />
            <DeleteModel
                isOpen={isModelOpen}
                onClose={() => {
                    handleCancel();
                }}
                name={
                    selectedItem?._name ||
                    selectedItem?._attachmentType ||
                    selectedItem?._obstacleType ||
                    itemType
                }
                onConfirm={() => handleConfirmDelete()}
            />
        </>
    );
});

export default ClosetData;

const TreeNode = observer(
    ({
        title,
        textClassName,
        handleDelete,
        handleFitToScreen,
        handleEdit,
        isFitToScreen,
    }) => {
        const { viewManager } = useMainContext();
        const isVisible = viewManager.doUserHasAccess();

        return (
            <div className="flex items-center gap-[8px] justify-between w-full ">
                <span
                    className={`text-[14px] text-theme-stroke ${textClassName}`}>
                    {title}
                </span>
                <div className="flex gap-[8px] items-center ">
                    {isFitToScreen && (
                        <div
                            className="cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleFitToScreen();
                            }}>
                            <AiOutlineExpandAlt className="text-[20px]" />
                        </div>
                    )}

                    {isVisible && (
                        <div
                            className="cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit();
                            }}>
                            <EditIcon />
                        </div>
                    )}
                    {isVisible && (
                        <div
                            className="cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete();
                            }}>
                            <DeleteIconSm />
                        </div>
                    )}
                </div>
            </div>
        );
    },
);
